import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, useMediaQuery } from '@hopskipdrive/react-components';
import { DuplicateRiderModal } from '@rideiq/riders/components/DuplicateRiderModal';
import { BreadcrumbsNav, UnsavedChangesModal } from '@rideiq/shared/components';
import { Button } from '@rideiq/shared/components/Button';
import { Text } from '@rideiq/shared/components/Text';
import { colorPalette, colorTokens, variables } from '@rideiq/shared/components/variables';
import styled from 'styled-components';

import { ConfirmationModal } from '../SharedBilling';

import { SectionAccordion } from './components/SectionAccordion';
import { SectionGutter } from './components/SectionGutter';
import { SectionTabs } from './components/SectionTabs';
import { useRiderFormAnalytics } from './hooks/useRiderFormAnalytics';
import { useUnsavedChangesGuard } from './hooks/useUnsavedChangesGuard';
import { BasicInfoSection } from './sections/BasicInfoSection';
import { ContactsSection } from './sections/ContactsSection';
import { LocationsSection } from './sections/LocationsSection/LocationsSection';
import { RiderServicesSection } from './sections/RiderServicesSection';
import { STICKY_HEADERS_HEIGHT } from './utils/breakpoints';
import { RiderFormValues, SECTION_KEYS, SECTION_LABELS, SECTION_SUBTITLES, SectionKey } from './types';
import { useRiderFormSubmit } from './useRiderFormSubmit';
import { useSectionScrollSpy } from './useSectionScrollSpy';

const ACCOUNT_BAR_HEIGHT = 38;
const MOBILE_NAV_HEIGHT = 62;

type Crumb = { title: string; pathname: string };

type SharedBillingCurrentRider = Pick<RiderFormType, 'isSharedBillingRider' | 'sharedBillingRider' | 'slug'>;

type RiderFormProps = {
  title: string;
  crumbs: Crumb[];
  onClose: () => void;
  mode: 'create' | 'edit';
  slug?: string;
  initialSection?: SectionKey;
  currentRider?: SharedBillingCurrentRider | null;
  onCreateConfirmation?: (slug: string) => void;
};

const SECTION_COMPONENTS: Record<SectionKey, React.ComponentType> = {
  'basic-info': BasicInfoSection,
  contacts: ContactsSection,
  'rider-services': RiderServicesSection,
  locations: LocationsSection,
};

export const RiderForm = ({
  title,
  crumbs,
  onClose,
  mode,
  slug,
  initialSection,
  currentRider,
  onCreateConfirmation,
}: RiderFormProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState<SectionKey>('basic-info');
  const [hasCreatedRider, setHasCreatedRider] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>(
    Object.fromEntries(SECTION_KEYS.map((key) => [key, true])) as Record<SectionKey, boolean>
  );
  const [pendingSubmit, setPendingSubmit] = useState<{
    values: RiderFormValues;
    dirtyFields: Partial<Record<keyof RiderFormValues, unknown>>;
  } | null>(null);
  const [needsSharedBillingConfirmation, setNeedsSharedBillingConfirmation] = useState(false);
  // Guards the whole submit chain (including the shared-billing confirmation step) against re-entrant Save clicks.
  const isSubmitInFlightRef = useRef(false);
  const [isSubmitInFlight, setIsSubmitInFlight] = useState(false);

  const releaseSubmitLock = useCallback(() => {
    isSubmitInFlightRef.current = false;
    setIsSubmitInFlight(false);
  }, []);

  const navigate = useNavigate();
  const {
    reportSectionViewed,
    reportSaveAttempted,
    reportSave,
    reportAbandoned,
    trackDirtyFields,
    reportUnsavedChangesModalShown,
    reportUnsavedChangesModalConfirmed,
    reportUnsavedChangesModalDismissed,
    reportFieldsEdited,
  } = useRiderFormAnalytics({
    action: mode,
    slug,
  });
  const { onSubmit, scrollToFirstError } = useRiderFormSubmit({
    mode,
    slug,
    reportSave,
    onCreateConfirmation,
    originalIsSharedBillingRider: currentRider?.isSharedBillingRider,
  });

  const handleActiveChange = useCallback(
    (section: SectionKey) => {
      setActiveSection(section);
      reportSectionViewed(section);
    },
    [reportSectionViewed]
  );

  useSectionScrollSpy({ onActiveChange: handleActiveChange });

  useEffect(() => {
    reportSectionViewed('basic-info');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const methods = useFormContext<RiderFormValues>();
  const { handleSubmit, formState } = methods;
  // Destructure during render to initialize the formState proxy subscriptions.
  // Accessing formState properties only inside callbacks leaves them unsubscribed,
  // which can cause stale reads in RHF v7's useFormContext proxy model.
  const { errors, isSubmitting, isDirty, isSubmitted, dirtyFields } = formState;
  const watchContacts = (useWatch<RiderFormValues, 'contacts'>({ name: 'contacts' }) ??
    []) as RiderFormValues['contacts'];

  const hasErrors = Object.keys(errors).length > 0;
  const hasContactWithPhone = watchContacts.some((c) => Boolean(c?.phoneNumber));

  useEffect(() => {
    trackDirtyFields(dirtyFields as Partial<Record<keyof RiderFormValues, unknown>>);
  }, [dirtyFields, trackDirtyFields]);

  const {
    isModalOpen: isUnsavedChangesModalOpen,
    guardNavigation,
    confirmDiscard,
    dismiss: dismissUnsavedChangesModal,
  } = useUnsavedChangesGuard({
    isDirty,
    onModalShown: reportUnsavedChangesModalShown,
    onDiscardConfirmed: () => {
      reportUnsavedChangesModalConfirmed();
      reportFieldsEdited();
    },
    onDismissed: reportUnsavedChangesModalDismissed,
  });

  const handleScrollTo = useCallback(
    (key: SectionKey) => {
      setExpandedSections((prev) => ({ ...prev, [key]: true }));
      setActiveSection(key);
      reportSectionViewed(key);
      requestAnimationFrame(() => {
        const el = scrollContainerRef.current?.querySelector(`#section-${key}`);

        el?.scrollIntoView({
          behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start',
        });
      });
    },
    [reportSectionViewed]
  );

  // Double-rAF: first frame waits for React to commit, second for the browser layout pass.
  useEffect(() => {
    let id2: number;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => {
        if (initialSection) {
          handleScrollTo(initialSection);
        }
      });
    });

    return () => {
      cancelAnimationFrame(id1);
      cancelAnimationFrame(id2);
    };
  }, [initialSection]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = useCallback(
    (key: SectionKey) => setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] })),
    []
  );

  const expandSection = useCallback((key: SectionKey) => setExpandedSections((prev) => ({ ...prev, [key]: true })), []);

  const handleFormSubmit = handleSubmit(
    async (values) => {
      if (isSubmitInFlightRef.current) {
        return;
      }

      isSubmitInFlightRef.current = true;
      setIsSubmitInFlight(true);

      reportSaveAttempted(false);
      const resolvedDirtyFields = dirtyFields as Partial<Record<keyof RiderFormValues, unknown>>;
      const sharedBillingActuallyChanged =
        mode === 'edit' && currentRider
          ? values.isSharedBillingRider !== currentRider.isSharedBillingRider ||
            (values.isSharedBillingRider && Boolean(resolvedDirtyFields.sharedBillingRider))
          : Boolean(resolvedDirtyFields.isSharedBillingRider || resolvedDirtyFields.sharedBillingRider);

      if (mode === 'edit' && sharedBillingActuallyChanged) {
        setPendingSubmit({ values, dirtyFields: resolvedDirtyFields });
        setNeedsSharedBillingConfirmation(true);

        return; // lock released by ConfirmationModal's onConfirm/onCancel below
      }

      try {
        const outcome = await onSubmit(values, resolvedDirtyFields);

        if (mode === 'create' && outcome.kind !== 'rider-error') {
          setHasCreatedRider(true);
        }

        if (outcome.kind === 'rider-error') {
          requestAnimationFrame(() => {
            scrollToFirstError(expandSection, methods.formState.errors);
          });
        }
      } finally {
        releaseSubmitLock();
      }
    },
    (validationErrors) => {
      reportSaveAttempted(true);
      scrollToFirstError(expandSection, validationErrors);
    }
  );

  const handleClose = useCallback(() => {
    guardNavigation(() => {
      if (isDirty) {
        reportAbandoned(activeSection);
      }
      onClose();
    });
  }, [guardNavigation, isDirty, activeSection, reportAbandoned, onClose]);

  const handleCrumbClick = useCallback(
    (event: React.MouseEvent, targetPath: string) => {
      if (!isDirty) {
        return;
      }
      event.preventDefault();
      guardNavigation(() => navigate(targetPath));
    },
    [isDirty, guardNavigation, navigate]
  );

  const saveDisabled = isSubmitting || isSubmitInFlight || !isDirty;
  const saveLabel = mode === 'create' ? 'Create Rider' : 'Save';
  const savingLabel = mode === 'create' ? 'Creating…' : 'Saving…';
  const isMobileNav = useMediaQuery({ maxWidth: variables.breakpoints.desktop - 1 });

  return (
    <>
      <Box
        sx={{
          ...(isMobileNav ? {} : { position: 'sticky', top: `${ACCOUNT_BAR_HEIGHT}px`, zIndex: 10 }),
          bgcolor: colorPalette.neutral99,
          borderBottom: `1px solid ${colorPalette.neutralVariant90}`,
          ml: isMobileNav ? '-16px' : '-32px',
          mr: isMobileNav ? '-16px' : '-24px',
          pl: isMobileNav ? '16px' : '32px',
          pr: isMobileNav ? '16px' : '24px',
          py: '10px',
        }}
      >
        <BreadcrumbsNav crumbs={crumbs} onCrumbClick={handleCrumbClick} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: `calc(100vh - ${STICKY_HEADERS_HEIGHT}px)`,
        }}
      >
        <FormBody>
          {!isMobileNav && (
            <Box
              sx={{
                position: 'sticky',
                top: `${STICKY_HEADERS_HEIGHT}px`,
                alignSelf: 'flex-start',
                pt: 2,
                pl: 2,
                pr: 2,
              }}
            >
              <SectionGutter activeSection={activeSection} onScrollTo={handleScrollTo} />
            </Box>
          )}
          {isMobileNav && (
            <Box
              sx={{
                position: 'sticky',
                top: `${ACCOUNT_BAR_HEIGHT + MOBILE_NAV_HEIGHT}px`,
                zIndex: 10,
                bgcolor: colorTokens.background.light,
                mx: '-16px',
                pl: '12px',
                borderBottom: `1px solid ${colorPalette.neutralVariant90}`,
              }}
            >
              <SectionTabs activeSection={activeSection} onScrollTo={handleScrollTo} />
            </Box>
          )}
          <Box sx={{ flex: 1, p: 2 }}>
            <ContentMaxWidth ref={scrollContainerRef as React.RefObject<HTMLDivElement>}>
              {isSubmitted && hasErrors && (
                <Box sx={{ mb: '1.375rem' }}>
                  <Alert
                    severity="error"
                    message={
                      hasContactWithPhone ? (
                        'Please fill in required fields.'
                      ) : (
                        <>
                          <div>Please fill in required fields.</div>
                          <div>At least one contact with a phone number is required.</div>
                        </>
                      )
                    }
                  />
                </Box>
              )}
              <Text variant="h1" sx={{ mb: '1.375rem' }}>
                {title}
              </Text>
              {SECTION_KEYS.map((key, index) => (
                <SectionAccordion
                  key={key}
                  id={key}
                  index={index + 1}
                  title={SECTION_LABELS[key]}
                  subtitle={SECTION_SUBTITLES[key]}
                  isExpanded={expandedSections[key]}
                  onToggle={() => handleToggle(key)}
                >
                  {(() => {
                    const SectionComponent = SECTION_COMPONENTS[key];

                    return <SectionComponent />;
                  })()}
                </SectionAccordion>
              ))}
            </ContentMaxWidth>
          </Box>
        </FormBody>
        {(mode === 'create' || isDirty) && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 3,
              py: 2,
              pl: isMobileNav ? '16px' : '32px',
              pr: isMobileNav ? '16px' : '24px',
              ml: isMobileNav ? '-16px' : '-32px',
              mr: isMobileNav ? '-16px' : '-24px',
              mb: '-16px',
              bgcolor: colorPalette.neutral99,
              borderTop: `1px solid ${colorPalette.neutralVariant90}`,
              zIndex: 10,
            }}
          >
            <Button
              color="tertiary"
              size="large"
              onClick={handleClose}
              data-testid="rider-form-cancel"
              style={isMobileNav ? { flex: 1, borderRadius: '20px' } : undefined}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              size="large"
              disabled={saveDisabled}
              data-testid="rider-form-save"
              onClick={handleFormSubmit}
              style={isMobileNav ? { flex: 1, borderRadius: '20px' } : undefined}
            >
              {isSubmitting || isSubmitInFlight ? savingLabel : saveLabel}
            </Button>
          </Box>
        )}
      </Box>
      {!isSubmitting && !isSubmitInFlight && !hasCreatedRider && <DuplicateRiderModal slug={slug} />}
      <UnsavedChangesModal
        open={isUnsavedChangesModalOpen}
        onDiscard={confirmDiscard}
        onKeepEditing={dismissUnsavedChangesModal}
      />
      {mode === 'edit' && currentRider && (
        <ConfirmationModal
          currentRider={currentRider}
          needsConfirmation={needsSharedBillingConfirmation}
          onConfirm={async () => {
            setNeedsSharedBillingConfirmation(false);

            try {
              if (pendingSubmit) {
                const { values, dirtyFields: pending } = pendingSubmit;

                setPendingSubmit(null);
                const outcome = await onSubmit(values, pending, values.isSharedBillingRider);

                if (outcome.kind === 'rider-error') {
                  requestAnimationFrame(() => {
                    scrollToFirstError(expandSection, methods.formState.errors);
                  });
                }
              }
            } finally {
              releaseSubmitLock();
            }
          }}
          onCancel={() => {
            setNeedsSharedBillingConfirmation(false);
            setPendingSubmit(null);
            releaseSubmitLock();
          }}
          riderStatus={currentRider.sharedBillingRider?.status ?? undefined}
        />
      )}
    </>
  );
};

const mqDesktop = `@media (max-width: ${variables.breakpoints.desktop - 1}px)`;

const FormBody = styled.div`
  display: flex;
  flex: 1;
  gap: 2.75rem;
  padding-top: 2rem;
  ${mqDesktop} {
    gap: 0;
    flex-direction: column;
    padding-top: 0;
  }
`;

const ContentMaxWidth = styled.div`
  max-width: 734px;
  ${mqDesktop} {
    max-width: 100%;
  }
`;
